import { autoinject, bindable, observable } from 'aurelia-framework';
import { Models } from 'models/core';
import { DetergentTypeService } from 'services/detergent-type-service';
import { DisinfectionTypeService } from 'services/disinfection-type-service';
import { ErrorService } from 'services/error-service';
import { LocationCodeService } from 'services/location-code-service';
import { ServiceConsumptionWashingService } from 'services/service-consumption-washing-service';
import { ServiceProductService } from 'services/service-product-service';

type Model = {
  DetergentTypeId: number | undefined;
  DetergentLocationCodeId: number | null | undefined;
  DetergentConsumption: number | undefined;
  DisinfectionTypeId: number | undefined;
  DisinfectionLocationCodeId: number | null | undefined;
  DisinfectionConsumption: number | undefined;
  Id?: number;
};

type data = { data: ServiceConsumptionWashingModel };

export type ServiceConsumptionWashingModel = {
  ServiceId: number;
  DetergentTypeId: number;
  DetergentLocationCodeId: number | null;
  DetergentConsumption: number;
  DisinfectionTypeId: number;
  DisinfectionLocationCodeId: number | null;
  DisinfectionConsumption: number;
  Id?: number;
};

@autoinject
export class ServiceWashingConsumptionCreateEdit {
  @bindable
  @observable
  model?: Model;
  targetModel: Model = {
    DetergentTypeId: 0,
    DetergentLocationCodeId: null,
    DetergentConsumption: undefined,
    DisinfectionTypeId: 0,
    DisinfectionLocationCodeId: null,
    DisinfectionConsumption: undefined,
  };
  modelChanged(model: Model) {
    this.targetModel = JSON.parse(JSON.stringify(model));

    this.form.DetergentTypeId = this.targetModel.DetergentTypeId;
    this.form.DetergentLocationCodeId = this.targetModel.DetergentLocationCodeId;
    this.form.DetergentConsumption = this.targetModel.DetergentConsumption;
    this.form.DisinfectionTypeId = this.targetModel.DisinfectionTypeId;
    this.form.DisinfectionLocationCodeId = this.targetModel.DisinfectionLocationCodeId;
    this.form.DisinfectionConsumption = this.targetModel.DisinfectionConsumption;
  }

  @bindable service!: Models.Service;

  @bindable onSave?: (data: data) => void | Promise<void>;
  @bindable onCanceled?: () => void;

  @bindable
  @observable
  visible = false;

  protected visibleChanged(newValue: boolean) {
    // When the form becomes visible in "create" mode (no existing model),
    // preselect the location codes based on the washing service station's
    // default location code (if configured).
    if (newValue && !this.model) {
      this.applyDefaultLocationCode();
    }
  }

  private applyDefaultLocationCode() {
    const defaultLocationCodeId =
      this.service?.ServiceStation?.DefaultLocationCodeId;

    if (!defaultLocationCodeId) {
      return;
    }

    if (!this.form.DetergentLocationCodeId) {
      this.form.DetergentLocationCodeId = defaultLocationCodeId;
    }
    if (!this.form.DisinfectionLocationCodeId) {
      this.form.DisinfectionLocationCodeId = defaultLocationCodeId;
    }
  }

  protected form: Model = {
    DetergentTypeId: undefined,
    DetergentLocationCodeId: undefined,
    DetergentConsumption: undefined,
    DisinfectionTypeId: undefined,
    DisinfectionLocationCodeId: undefined,
    DisinfectionConsumption: undefined,
  };

  constructor(
    private errorService: ErrorService,
    protected serviceConsumptionWashingService: ServiceConsumptionWashingService,
    protected detergentTypeService: DetergentTypeService,
    protected disinfectionTypeService: DisinfectionTypeService,
    private serviceProductservice: ServiceProductService,
    private locationCodeService: LocationCodeService,
  ) {}

  protected async getDetergentTypes() {
    const result = await this.serviceProductservice.getDetergentTypes('Washing', this.service.Id);
    return result.data;
  }
  protected async getDisinfectionTypes() {
    const result = await this.serviceProductservice.getDisinfectionTypes('Washing', this.service.Id);
    return result.data;
  }

  private createModel() {
    const source = this.targetModel;
    const model: ServiceConsumptionWashingModel = {
      ServiceId: this.service.Id,
      DetergentTypeId: this.form.DetergentTypeId!,
      DetergentLocationCodeId: this.form.DetergentLocationCodeId || null,
      DetergentConsumption: this.form.DetergentConsumption!,
      DisinfectionTypeId: this.form.DisinfectionTypeId!,
      DisinfectionLocationCodeId: this.form.DisinfectionLocationCodeId || null,
      DisinfectionConsumption: this.form.DisinfectionConsumption!,
    };

    if (source.Id) {
      model.Id = source.Id;
    }

    return model;
  }

  async save() {
    try {
      await this.onSave?.({
        data: this.createModel(),
      });
      this.resetForm();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  resetForm() {
    this.form.DetergentTypeId = 0;
    this.form.DetergentLocationCodeId = null;
    this.form.DetergentConsumption = undefined;
    this.form.DisinfectionTypeId = 0;
    this.form.DisinfectionLocationCodeId = null;
    this.form.DisinfectionConsumption = undefined;

    // Re-apply the service station's default location code so a subsequent
    // "create" starts with the preselected value again.
    this.applyDefaultLocationCode();
  }

  cancel() {
    this.onCanceled?.();
  }
}
